import { useMemo, useCallback } from 'react';
import { pick, compareObjects } from 'js/utils/ramda.util';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as helpers from '../helpers';
import { PRODUCTS } from '../constants';
import * as actions from '../store/actions';
import { useValidate } from './useValidate';
import { useDynamicReducerSelector, useDynamicReducerDispatch } from '../store';

export const useTableHeader = () => {
    const dispatch = useDynamicReducerDispatch();
    const translate = useComponentTranslations();
    const { product, from, until, isLoading } = useDynamicReducerSelector(pick([
        'product', 'from', 'until', 'isLoading'
    ]));
    const productOptions = useMemo(() => Object.values(PRODUCTS).map(option => ({
        label: translate(`products.${option}`), value: option
    })), [translate]);

    const onProductChange = useCallback((event) => {
        dispatch(actions.setProduct(event.target.value));
    }, [dispatch]);

    const headerInitialValues = useMemo(() => ({
        product,
        from,
        until
    }), [product, from, until]);

    const headerValidate = useValidate();
    const getIsDisabled = useCallback(values => {
        const startDateToCompare = helpers.intoDate(values.from);
        const endDateToCompare = helpers.intoDate(values.until);

        return isLoading
            || compareObjects(from, startDateToCompare)
            && compareObjects(until, endDateToCompare)
            || compareObjects(helpers.startOfDay(from), startDateToCompare)
            && compareObjects(helpers.startOfDay(until), endDateToCompare);
    }, [isLoading, from, until]);

    const anotherPeriodFilter = useMemo(() => ({
        from: { label: translate('filter.from'), disabled: isLoading },
        until: { label: translate('filter.until'), disabled: isLoading },
        submit: {
            title: translate('filter.btn.apply'),
            getIsDisabled
        }
    }), [translate, getIsDisabled, isLoading]);

    const onAnotherPeriodApply = useCallback((values) => {
        dispatch(actions.setAnotherPeriod({
            from: helpers.startOfDay(values.from),
            until: helpers.endOfDay(values.until)
        }));
    }, [dispatch]);

    return useMemo(() => ({
        filters: [{
            id: 0,
            name: 'product',
            disabled: isLoading,
            options: productOptions,
            onChange: onProductChange,
            label: translate('filter.product'),
            'aria-label': translate(['filter.product', 'playtech.mobile.system.select.aria-label'])
        }],
        headerValidate,
        headerInitialValues,
        anotherPeriodFilter,
        onAnotherPeriodApply
    }), [
        isLoading, translate, productOptions, onAnotherPeriodApply,
        onProductChange, headerValidate, headerInitialValues, anotherPeriodFilter
    ]);
};
